import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  TextField,
  Button,
  Typography,
  Chip,
  IconButton,
  Avatar,
  InputAdornment,
  Tooltip,
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Videocam as VideocamIcon,
  MoreVert as MoreVertIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
  Schedule as ScheduleIcon,
  Gavel as GavelIcon,
  FiberManualRecord as DotIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon,
  NotificationsOff as NotificationsOffIcon,
  NotificationsActive as NotificationsActiveIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  MarkChatUnread as MarkChatUnreadIcon,
  MarkChatRead as MarkChatReadIcon,
} from '@mui/icons-material';
import { format, isValid, parseISO } from 'date-fns';
import { conversationsApi } from '../../api/conversations';
import { ChatTranscript } from '../../components/chat/ChatTranscript';
import { ChatAvatar } from '../../components/chat/ChatAvatar';
import { MessageComposer } from '../../components/chat/MessageComposer';
import { useToast } from '../../hooks/useToast';
import { useMessageStream } from '../../hooks/useMessageStream';

const K = {
  navy: '#121826',
  blue: '#4185F1',
  green: '#6BD56F',
  border: 'rgba(18, 24, 38, 0.08)',
  inputBg: '#EEF1F8',
  listBg: '#FAFBFC',
};

const listItemTime = (conv) => {
  const raw = conv.updatedAt || conv.lastMessageAt || conv.lastActivity;
  if (!raw) return null;
  try {
    const d = typeof raw === 'string' ? parseISO(raw) : new Date(raw);
    if (!isValid(d)) return null;
    return format(d, 'HH:mm');
  } catch {
    return null;
  }
};

export const Inbox = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [listTab, setListTab] = useState('all');
  const [replyTo, setReplyTo] = useState(null);
  const [forwardSource, setForwardSource] = useState(null);
  const [actionAnchor, setActionAnchor] = useState(null); // { el, convId }
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [matchIndex, setMatchIndex] = useState(0);
  const [assignAnchor, setAssignAnchor] = useState(null);

  const { data: operators = [] } = useQuery({
    queryKey: ['operators'],
    queryFn: conversationsApi.listOperators,
    staleTime: 5 * 60 * 1000,
  });
  const [filters, setFilters] = useState({
    assigned: '',
    tags: [],
    search: '',
  });
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  useMessageStream(selectedConversation);

  const apiFilters = useMemo(
    () => ({
      ...filters,
      status: listTab === 'resolved' ? 'resolved' : '',
      onlyArchived: listTab === 'archived',
    }),
    [filters, listTab]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['conversations', apiFilters],
    queryFn: () => conversationsApi.getConversations(apiFilters),
  });

  const displayConversations = useMemo(() => {
    let rows = data?.data || [];
    if (listTab === 'unread') {
      rows = rows.filter(
        (c) => (c.unreadCount || 0) > 0 || c.manuallyUnread === true,
      );
    }
    return rows;
  }, [data, listTab]);

  const normalizedConversationId =
    selectedConversation !== null && selectedConversation !== undefined
      ? String(selectedConversation)
      : null;

  const { data: conversation, isLoading: conversationLoading } = useQuery({
    queryKey: ['conversation', normalizedConversationId],
    queryFn: () => conversationsApi.getConversation(normalizedConversationId),
    enabled: !!normalizedConversationId,
  });

  const sendMessageMutation = useMutation({
    mutationFn: ({ conversationId, opts }) =>
      conversationsApi.sendMessage(conversationId, opts),
    onSuccess: () => {
      if (normalizedConversationId) {
        queryClient.invalidateQueries(['conversation', normalizedConversationId]);
      }
      queryClient.invalidateQueries(['conversations']);
    },
    onError: (error) => {
      showToast(error.message, 'error');
    },
  });

  const updateConversationMutation = useMutation({
    mutationFn: ({ id, updates }) => conversationsApi.updateConversation(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['conversations']);
      if (normalizedConversationId) {
        queryClient.invalidateQueries(['conversation', normalizedConversationId]);
      }
      showToast('Conversación actualizada', 'success');
    },
    onError: (error) => {
      showToast(error.message, 'error');
    },
  });

  const handleSelectConversation = (id) => {
    setSelectedConversation(id);
    queryClient.setQueriesData({ queryKey: ['conversations'], exact: false }, (old) => {
      if (!old || !Array.isArray(old.data)) return old;
      const sid = String(id);
      let touched = false;
      const data = old.data.map((c) => {
        if (String(c.id) !== sid && String(c.phone) !== sid) return c;
        if (!c.unreadCount && !c.manuallyUnread) return c;
        touched = true;
        return { ...c, unreadCount: 0, manuallyUnread: false };
      });
      return touched ? { ...old, data } : old;
    });
  };

  const handleSendMessage = (opts) => {
    if (normalizedConversationId) {
      sendMessageMutation.mutate({ conversationId: normalizedConversationId, opts });
    }
  };

  const handleEditMessage = async (msg, newText) => {
    try {
      await conversationsApi.editMessage(msg.id, newText);
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handleDeleteMessage = async (msg) => {
    if (!window.confirm('¿Eliminar este mensaje solo para vos?')) return;
    try {
      await conversationsApi.hideMessage(msg.id);
      queryClient.setQueryData(['conversation', normalizedConversationId], (old) => {
        if (!old || !Array.isArray(old.messages)) return old;
        return { ...old, messages: old.messages.filter((m) => m.id !== msg.id) };
      });
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const forwardMutation = useMutation({
    mutationFn: ({ messageId, receivers }) =>
      conversationsApi.forwardMessage(messageId, receivers),
    onSuccess: () => {
      queryClient.invalidateQueries(['conversations']);
      showToast('Mensaje reenviado', 'success');
      setForwardSource(null);
    },
    onError: (error) => showToast(error.message, 'error'),
  });

  const handleProfilePhotoChange = async (file) => {
    if (!normalizedConversationId) return;
    try {
      const uploaded = await conversationsApi.uploadMedia(file, 'image');
      await conversationsApi.updateConversation(normalizedConversationId, {
        profile_photo_media_id: uploaded.mediaId,
      });
      queryClient.invalidateQueries(['conversation', normalizedConversationId]);
      queryClient.invalidateQueries(['conversations']);
    } catch (e) {
      showToast(e.message || 'Error al subir la foto', 'error');
    }
  };

  const conversationActionMutation = useMutation({
    mutationFn: ({ id, updates }) => conversationsApi.updateConversation(id, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(['conversations']);
      queryClient.invalidateQueries(['conversation', String(variables.id)]);
    },
    onError: (error) => showToast(error.message, 'error'),
  });
  const handleConversationAction = (id, updates) =>
    conversationActionMutation.mutate({ id, updates });

  const handleLoadOlder = async () => {
    if (!normalizedConversationId || !conversation?.hasMoreMessages || loadingOlder) return;
    try {
      setLoadingOlder(true);
      const currentMessages = Array.isArray(conversation.messages) ? conversation.messages : [];
      const res = await conversationsApi.getMessages(normalizedConversationId, {
        before: conversation.nextBefore,
        limit: 50,
      });

      const existingIds = new Set(currentMessages.map((m) => m.id));
      const olderUnique = (res.messages || []).filter((m) => !existingIds.has(m.id));
      const merged = [...olderUnique, ...currentMessages];
      merged.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      queryClient.setQueryData(['conversation', normalizedConversationId], (old) => {
        if (!old) return old;
        return {
          ...old,
          messages: merged,
          hasMoreMessages: !!res.hasMore,
          nextBefore: res.nextBefore || null,
        };
      });
    } catch (e) {
      showToast(e.message || 'Error cargando mensajes anteriores', 'error');
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleResolve = () => {
    if (selectedConversation) {
      updateConversationMutation.mutate({
        id: selectedConversation,
        updates: { status: 'resolved' },
      });
    }
  };

  const handleEscalate = () => {
    if (selectedConversation) {
      updateConversationMutation.mutate({
        id: selectedConversation,
        updates: { status: 'escalated' },
      });
    }
  };

  const chipSx = (active) => ({
    height: 32,
    borderRadius: 1.5,
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'none',
    ...(active
      ? {
          backgroundColor: K.blue,
          color: '#fff',
          border: 'none',
          '& .MuiChip-label': {
            color: '#fff',
          },
          '&:hover': {
            backgroundColor: '#3574d9',
            '& .MuiChip-label': { color: '#fff' },
          },
        }
      : {
          backgroundColor: '#fff',
          color: K.navy,
          border: `1px solid ${K.border}`,
          '& .MuiChip-label': {
            color: K.navy,
          },
          '&:hover': { backgroundColor: K.inputBg },
        }),
  });

  const conversationChips = (conv) => {
    const chips = [];
    if (conv.status === 'escalated') {
      chips.push(
        <Chip
          key="u"
          label="Urgente"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: alpha('#FF5252', 0.12),
            color: '#C62828',
          }}
        />
      );
    }
    if (conv.status === 'open') {
      chips.push(
        <Chip
          key="c"
          label="Cobro pendiente"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: alpha(K.green, 0.25),
            color: '#1B5E20',
          }}
        />
      );
    }
    if (conv.status === 'resolved') {
      chips.push(
        <Chip
          key="r"
          label="Resuelto"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: alpha(K.blue, 0.12),
            color: K.blue,
          }}
        />
      );
    }
    if (conv.status === 'in_progress') {
      chips.push(
        <Chip
          key="p"
          label="En curso"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: 'rgba(18, 24, 38, 0.06)',
            color: K.navy,
          }}
        />
      );
    }
    return chips;
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1600,
        alignSelf: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      {/* Shell: three columns */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          border: `1px solid ${K.border}`,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          boxShadow: '0 1px 3px rgba(18, 24, 38, 0.06)',
          // Lock height to viewport so the chat transcript scrolls inside and the composer stays visible
          minHeight: { xs: 'auto', md: 'calc(100vh - 112px)' },
          height: { xs: 'auto', md: 'calc(100vh - 112px)' },
          maxHeight: { xs: 'none', md: 'calc(100vh - 112px)' },
        }}
      >
        {/* —— Column 1: Inbox list —— */}
        <Box
          sx={{
            width: { xs: '100%', md: 360 },
            flexShrink: 0,
            borderRight: { md: `1px solid ${K.border}` },
            bgcolor: K.listBg,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 360, md: 0 },
            maxHeight: { xs: 480, md: '100%' },
            height: { md: '100%' },
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${K.border}`,
              bgcolor: '#fff',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy, letterSpacing: '-0.02em' }}>
              Inbox
            </Typography>
            <Tooltip title="Nuevo mensaje">
              <IconButton size="small" sx={{ color: K.blue }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ p: 2, pb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar clientes…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(18,24,38,0.38)', fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: K.inputBg,
                  '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.08)' },
                  '&:hover fieldset': { borderColor: `${K.blue}55` },
                  '&.Mui-focused fieldset': { borderColor: K.blue, borderWidth: 2 },
                },
              }}
            />
          </Box>

          <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[
              { id: 'all', label: 'Todos' },
              { id: 'unread', label: 'Sin leer' },
              { id: 'resolved', label: 'Resueltos' },
              { id: 'archived', label: 'Archivados' },
            ].map((t) => (
              <Chip
                key={t.id}
                label={t.label}
                color="default"
                onClick={() => setListTab(t.id)}
                sx={chipSx(listTab === t.id)}
              />
            ))}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, pb: 2 }}>
            {isLoading ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Cargando…
              </Typography>
            ) : displayConversations.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No hay conversaciones
              </Typography>
            ) : (
              displayConversations.map((conv) => {
                const id = String(conv.id);
                const selected = normalizedConversationId === id;
                const initial = (conv.customerName || conv.phone || '?').charAt(0).toUpperCase();
                const time = listItemTime(conv);
                return (
                  <Box
                    key={id}
                    onClick={() => handleSelectConversation(id)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      gap: 1.5,
                      p: 1.5,
                      mb: 1,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      bgcolor: selected ? '#fff' : 'transparent',
                      border: '1px solid',
                      borderColor: selected ? alpha(K.blue, 0.25) : 'transparent',
                      borderLeftWidth: 4,
                      borderLeftColor: selected ? K.blue : 'transparent',
                      boxShadow: selected ? '0 2px 8px rgba(65, 133, 241, 0.12)' : 'none',
                      transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
                      '& .row-actions': { opacity: 0, transition: 'opacity .15s' },
                      '&:hover .row-actions': { opacity: 1 },
                      '&:hover': {
                        bgcolor: '#fff',
                        boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
                      },
                    }}
                  >
                    <ChatAvatar
                      mediaId={conv.profilePhotoMediaId}
                      name={conv.customerName || conv.phone}
                      size={44}
                      active={selected}
                      bgColorActive={alpha(K.blue, 0.15)}
                      fgColorActive={K.blue}
                      fgColor={K.navy}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          {conv.pinnedAt && <PushPinIcon sx={{ fontSize: 14, color: K.navy }} />}
                          {conv.mutedUntil && <NotificationsOffIcon sx={{ fontSize: 14, color: 'rgba(18,24,38,0.5)' }} />}
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: K.navy }} noWrap>
                            {conv.customerName || conv.phone || 'Sin nombre'}
                          </Typography>
                        </Box>
                        {time && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                            {time}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '0.8125rem',
                          lineHeight: 1.35,
                        }}
                      >
                        {conv.lastMessage || 'Sin mensajes aún'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, alignItems: 'center' }}>
                        {conversationChips(conv)}
                        {(conv.unreadCount > 0 || conv.manuallyUnread) && (
                          <Box sx={{
                            ml: 'auto',
                            minWidth: 18, height: 18, px: 0.75,
                            borderRadius: '999px', bgcolor: K.blue, color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 700,
                          }}>
                            {conv.unreadCount > 0 ? conv.unreadCount : ''}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      className="row-actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionAnchor({ el: e.currentTarget, convId: id });
                      }}
                      sx={{
                        position: 'absolute',
                        top: 6, right: 6,
                        color: 'rgba(18,24,38,0.6)',
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* —— Column 2: Chat —— */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: { md: 0 },
            display: 'flex',
            flexDirection: 'column',
            borderRight: { md: `1px solid ${K.border}` },
            bgcolor: '#fff',
            overflow: 'hidden',
            height: { md: '100%' },
          }}
        >
          {!normalizedConversationId ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                bgcolor: '#FAFBFC',
              }}
            >
              <Typography color="text.secondary" align="center">
                Selecciona una conversación para ver los mensajes
              </Typography>
            </Box>
          ) : conversationLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Cargando…</Typography>
            </Box>
          ) : conversation ? (
            <>
              <Box
                sx={{
                  flexShrink: 0,
                  px: 2,
                  py: 1.75,
                  borderBottom: `1px solid ${K.border}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                {searchOpen ? (
                  <SearchInChat
                    messages={conversation.messages || []}
                    text={searchText}
                    onText={(t) => { setSearchText(t); setMatchIndex(0); }}
                    matchIndex={matchIndex}
                    onMatchIndex={setMatchIndex}
                    onClose={() => { setSearchOpen(false); setSearchText(''); setMatchIndex(0); }}
                  />
                ) : (
                  <>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: K.navy }}>
                        {conversation.customerName || conversation.phone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {conversation.phone}
                        {conversation.companyName ? ` · ${conversation.companyName}` : ''}
                      </Typography>
                      <Chip
                        size="small"
                        label={
                          conversation.assignedToName
                            ? `Asignado a ${conversation.assignedToName}`
                            : 'Sin asignar'
                        }
                        onClick={(e) => setAssignAnchor(e.currentTarget)}
                        sx={{
                          mt: 0.5,
                          cursor: 'pointer',
                          height: 22,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          bgcolor: conversation.assignedToUserId
                            ? alpha(K.blue, 0.12)
                            : 'rgba(18,24,38,0.06)',
                          color: conversation.assignedToUserId ? K.blue : K.navy,
                        }}
                      />
                      {conversation.daysOverdue != null && (
                        <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700, mt: 0.5, display: 'block' }}>
                          Mora: {conversation.daysOverdue} días
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" sx={{ color: 'rgba(18,24,38,0.45)' }} onClick={() => setSearchOpen(true)}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: 'rgba(18,24,38,0.45)' }}>
                        <PhoneIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: 'rgba(18,24,38,0.45)' }}>
                        <VideocamIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: 'rgba(18,24,38,0.45)' }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Box>

              <Box
                sx={{
                  flex: '1 1 0',
                  minHeight: { md: 0 },
                  maxHeight: { md: '100%' },
                  bgcolor: '#F4F6F9',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <ChatTranscript
                  messages={conversation.messages || []}
                  hasMore={conversation.hasMoreMessages}
                  loadingOlder={loadingOlder}
                  onLoadOlder={handleLoadOlder}
                  onEdit={handleEditMessage}
                  onForward={(msg) => setForwardSource(msg)}
                  onDelete={handleDeleteMessage}
                  onReply={(msg) => {
                    const previewByType = {
                      image: '📷 Foto',
                      video: '🎥 Video',
                      audio: msg.voice ? '🎤 Nota de voz' : '🔊 Audio',
                      document: msg.filename ? `📎 ${msg.filename}` : '📎 Documento',
                      location: '📍 Ubicación',
                      contacts: '👤 Contacto',
                    };
                    setReplyTo({
                      id: msg.id,
                      preview: msg.content || msg.caption || previewByType[msg.messageType] || '',
                      content: msg.content,
                    });
                  }}
                />
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  px: 1.5,
                  py: 1,
                  background: `linear-gradient(180deg, ${alpha(K.green, 0.2)} 0%, ${alpha(K.green, 0.08)} 100%)`,
                  borderTop: `1px solid ${alpha(K.green, 0.35)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 20, color: K.navy }} />
                <Typography variant="caption" sx={{ fontWeight: 800, color: K.navy, letterSpacing: '0.04em' }}>
                  CONVERSACIÓN PROTEGIDA POR KOBRA IA
                </Typography>
              </Box>

              <Box sx={{ flexShrink: 0, p: 2, borderTop: `1px solid ${K.border}` }}>
                <MessageComposer
                  onSend={handleSendMessage}
                  onUpload={conversationsApi.uploadMedia}
                  disabled={sendMessageMutation.isLoading}
                  replyTo={replyTo}
                  onCancelReply={() => setReplyTo(null)}
                />
              </Box>
            </>
          ) : null}
        </Box>

        {/* —— Column 3: Customer details —— */}
        <Box
          sx={{
            width: { xs: '100%', md: 300 },
            flexShrink: 0,
            bgcolor: K.listBg,
            display: 'flex',
            flexDirection: 'column',
            borderTop: { xs: `1px solid ${K.border}`, md: 'none' },
            minHeight: { md: 0 },
            height: { md: '100%' },
            maxHeight: { md: '100%' },
            overflowY: 'auto',
          }}
        >
          {!normalizedConversationId || !conversation ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Detalles del cliente
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Selecciona un chat para ver la ficha
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  px: 2,
                  pt: 2,
                  pb: 1,
                  color: 'text.secondary',
                  letterSpacing: '0.12em',
                  fontWeight: 700,
                }}
              >
                Detalles del cliente
              </Typography>
              <Box sx={{ px: 2, pb: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <ChatAvatar
                    mediaId={conversation.profilePhotoMediaId}
                    name={conversation.customerName || conversation.phone}
                    size={72}
                    active
                    bgColorActive={alpha(K.blue, 0.15)}
                    fgColorActive={K.blue}
                    onChange={handleProfilePhotoChange}
                  />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: K.navy }}>
                  {conversation.customerName || 'Cliente'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {conversation.companyName || conversation.phone || '—'}
                </Typography>
              </Box>

              <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 1.5, pb: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                    border: `1px solid ${K.border}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Balance pendiente
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'error.main', mt: 0.5 }}>
                    {conversation.balance != null
                      ? new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(
                          conversation.balance
                        )
                      : '—'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                    border: `1px solid ${K.border}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Último pago
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: K.navy, mt: 0.5 }}>
                    {conversation.lastPaymentDate || '—'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: '#fff',
                    border: `1px solid ${K.border}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Próximo recordatorio
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: K.blue, mt: 0.5 }}>
                    {conversation.nextReminder || '—'}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="overline"
                sx={{
                  px: 2,
                  pb: 1,
                  color: 'text.secondary',
                  letterSpacing: '0.08em',
                  fontWeight: 700,
                }}
              >
                Acciones rápidas
              </Typography>
              <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 1, pb: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<ChevronRightIcon />}
                  onClick={handleResolve}
                  disabled={conversation.status === 'resolved'}
                  sx={{
                    justifyContent: 'space-between',
                    borderRadius: 1.5,
                    py: 1.25,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#2E7D32',
                    borderColor: alpha(K.green, 0.55),
                    bgcolor: '#fff',
                  }}
                >
                  Marcar como resuelto
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => showToast('Generación de enlace próximamente', 'info')}
                  sx={{
                    justifyContent: 'space-between',
                    borderRadius: 1.5,
                    py: 1.25,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: K.blue,
                    borderColor: alpha(K.blue, 0.35),
                    bgcolor: '#fff',
                  }}
                  startIcon={<LinkIcon />}
                >
                  Generar link
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<ChevronRightIcon />}
                  onClick={() => showToast('Reprogramación próximamente', 'info')}
                  sx={{
                    justifyContent: 'space-between',
                    borderRadius: 1.5,
                    py: 1.25,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: K.navy,
                    borderColor: K.border,
                    bgcolor: '#fff',
                  }}
                  startIcon={<ScheduleIcon />}
                >
                  Reprogramar cobro
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<ChevronRightIcon />}
                  onClick={handleEscalate}
                  disabled={conversation.status === 'escalated'}
                  sx={{
                    justifyContent: 'space-between',
                    borderRadius: 1.5,
                    py: 1.25,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: 'error.main',
                    borderColor: alpha('#c62828', 0.35),
                    bgcolor: '#fff',
                  }}
                  startIcon={<GavelIcon />}
                >
                  Escalar a jurídico
                </Button>
              </Box>

              <Box sx={{ p: 2, mt: 'auto' }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: K.navy,
                    color: '#fff',
                  }}
                >
                  <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: '0.1em' }}>
                    Estado del canal
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                    Chat en vivo con IA activa
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
                    <DotIcon sx={{ fontSize: 12, color: K.green }} />
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Sincronizado
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <ForwardDialog
        source={forwardSource}
        conversations={data?.data || []}
        onClose={() => setForwardSource(null)}
        onConfirm={(receivers) =>
          forwardMutation.mutate({ messageId: forwardSource?.id, receivers })
        }
        loading={forwardMutation.isLoading}
      />

      <ConversationActionMenu
        anchor={actionAnchor}
        onClose={() => setActionAnchor(null)}
        conversation={(data?.data || []).find((c) => String(c.id) === actionAnchor?.convId)}
        onAction={(updates) => {
          if (actionAnchor?.convId) handleConversationAction(actionAnchor.convId, updates);
          setActionAnchor(null);
        }}
      />

      <Menu
        anchorEl={assignAnchor}
        open={!!assignAnchor}
        onClose={() => setAssignAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (normalizedConversationId) {
              handleConversationAction(normalizedConversationId, { assigned_to_user_id: null });
            }
            setAssignAnchor(null);
          }}
        >
          <ListItemText>Sin asignar</ListItemText>
        </MenuItem>
        {operators.map((op) => (
          <MenuItem
            key={op.id}
            selected={conversation?.assignedToUserId === op.id}
            onClick={() => {
              if (normalizedConversationId) {
                handleConversationAction(normalizedConversationId, { assigned_to_user_id: op.id });
              }
              setAssignAnchor(null);
            }}
          >
            <ListItemText primary={op.name} secondary={op.email} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const SearchInChat = ({ messages, text, onText, matchIndex, onMatchIndex, onClose }) => {
  const matchIds = React.useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return [];
    return messages
      .filter((m) => {
        const hay = `${m.content || ''} ${m.caption || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .map((m) => String(m.id));
  }, [messages, text]);

  React.useEffect(() => {
    if (matchIds.length === 0) return;
    const id = matchIds[Math.min(matchIndex, matchIds.length - 1)];
    const el = document.querySelector(`[data-mid="${id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [matchIds, matchIndex]);

  const move = (delta) => {
    if (matchIds.length === 0) return;
    const next = (matchIndex + delta + matchIds.length) % matchIds.length;
    onMatchIndex(next);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
      <SearchIcon fontSize="small" sx={{ color: 'rgba(18,24,38,0.5)' }} />
      <TextField
        autoFocus
        fullWidth
        size="small"
        placeholder="Buscar en la conversación…"
        value={text}
        onChange={(e) => onText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') move(e.shiftKey ? -1 : 1);
          else if (e.key === 'Escape') onClose();
        }}
        variant="standard"
        InputProps={{ disableUnderline: true }}
      />
      <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {matchIds.length === 0 ? '0' : `${matchIndex + 1} de ${matchIds.length}`}
      </Typography>
      <IconButton size="small" disabled={matchIds.length === 0} onClick={() => move(-1)}>
        <ChevronRightIcon sx={{ transform: 'rotate(-90deg)' }} fontSize="small" />
      </IconButton>
      <IconButton size="small" disabled={matchIds.length === 0} onClick={() => move(1)}>
        <ChevronRightIcon sx={{ transform: 'rotate(90deg)' }} fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={onClose}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>×</Typography>
      </IconButton>
    </Box>
  );
};

const ConversationActionMenu = ({ anchor, onClose, conversation, onAction }) => {
  if (!conversation) return null;
  const pinned = !!conversation.pinnedAt;
  const muted = !!conversation.mutedUntil;
  const archived = !!conversation.archivedAt;
  const muteFor = (hours) => {
    if (hours == null) return onAction({ muted_until: null });
    const until = new Date(Date.now() + hours * 3600 * 1000).toISOString();
    onAction({ muted_until: until });
  };
  return (
    <Menu anchorEl={anchor?.el} open={!!anchor} onClose={onClose}>
      <MenuItem onClick={() => onAction({ pinned: !pinned })}>
        <ListItemIcon>{pinned ? <PushPinOutlinedIcon fontSize="small" /> : <PushPinIcon fontSize="small" />}</ListItemIcon>
        <ListItemText>{pinned ? 'Desanclar' : 'Anclar'}</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => onAction({ manually_unread: !conversation.manuallyUnread })}>
        <ListItemIcon>
          {conversation.manuallyUnread ? <MarkChatReadIcon fontSize="small" /> : <MarkChatUnreadIcon fontSize="small" />}
        </ListItemIcon>
        <ListItemText>
          {conversation.manuallyUnread ? 'Marcar como leído' : 'Marcar como no leído'}
        </ListItemText>
      </MenuItem>
      {muted ? (
        <MenuItem onClick={() => muteFor(null)}>
          <ListItemIcon><NotificationsActiveIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Activar notificaciones</ListItemText>
        </MenuItem>
      ) : (
        [
          <MenuItem key="m1" onClick={() => muteFor(8)}>
            <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Silenciar 8 h</ListItemText>
          </MenuItem>,
          <MenuItem key="m2" onClick={() => muteFor(24)}>
            <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Silenciar 1 día</ListItemText>
          </MenuItem>,
          <MenuItem key="m3" onClick={() => muteFor(24 * 365 * 10)}>
            <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Silenciar siempre</ListItemText>
          </MenuItem>,
        ]
      )}
      <MenuItem onClick={() => onAction({ archived: !archived })}>
        <ListItemIcon>{archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}</ListItemIcon>
        <ListItemText>{archived ? 'Desarchivar' : 'Archivar'}</ListItemText>
      </MenuItem>
    </Menu>
  );
};

const ForwardDialog = ({ source, conversations, onClose, onConfirm, loading }) => {
  const [picked, setPicked] = React.useState(new Set());
  React.useEffect(() => { setPicked(new Set()); }, [source?.id]);
  const toggle = (id) => {
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  return (
    <MuiDialog open={!!source} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Reenviar mensaje</DialogTitle>
      <DialogContent dividers sx={{ p: 0, maxHeight: 380 }}>
        <List dense>
          {conversations.map((c) => {
            const id = String(c.id);
            const checked = picked.has(id);
            return (
              <ListItemButton key={id} onClick={() => toggle(id)}>
                <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                <ListItemText
                  primary={c.customerName || c.phone}
                  secondary={c.phone}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={picked.size === 0 || loading}
          onClick={() => onConfirm(Array.from(picked))}
        >
          Reenviar a {picked.size || ''}
        </Button>
      </DialogActions>
    </MuiDialog>
  );
};
